/**
 * Platform Admin External Services / Infrastructure Registry (P0-B).
 *
 * "外部服务" 总览 + 详情 Drawer — every Provider/Service/Environment/Plan/
 * Status/Usage/Quota/Billing/Renewal/API同步 value rendered here comes
 * straight from `GET /api/saas/platform/external-services` (backend:
 * `external_service_registry_service.list_services` +
 * `app.saas.services.infrastructure` Provider Adapters) — this page never
 * hardcodes a provider-specific field, so a future adapter (Stripe/OpenAI/
 * Google/Anthropic) needs no frontend change beyond appearing in the list.
 *
 * `usage`/`quota`/`metadata` are rendered as a generic, sorted key/value
 * list rather than named columns, on purpose (spec §24) — a real value
 * that happens to be missing/`null`/`not available` renders as "—", never
 * fabricated.
 */
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Drawer, Table, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { platformListExternalServices, platformRefreshExternalService } from '../api'
import type { ExternalServiceHealth, ExternalServiceRow, ExternalServicesSummary } from '../types'
import { SectionCard, SummaryCard, formatDateTime, formatRelativeTime } from './adminShared'
import { cardIconBg, platformColors } from './theme'

const AUTO_REFRESH_MS = 30_000

const HEALTH_TONE: Record<ExternalServiceHealth, { bg: string; color: string; label: string; icon: React.ReactNode }> = {
  healthy: { bg: platformColors.successBg, color: platformColors.success, label: 'Healthy', icon: <CheckCircleOutlined /> },
  warning: { bg: platformColors.warningBg, color: platformColors.warning, label: 'Warning', icon: <ExclamationCircleOutlined /> },
  critical: { bg: platformColors.dangerBg, color: platformColors.danger, label: 'Critical', icon: <StopOutlined /> },
  unknown: { bg: '#F1F5F9', color: platformColors.secondaryText, label: 'Unknown', icon: <QuestionCircleOutlined /> },
  not_configured: { bg: '#F1F5F9', color: platformColors.mutedText, label: 'Not configured', icon: <QuestionCircleOutlined /> },
}

const SOURCE_LABEL: Record<string, string> = {
  provider_api: 'Provider API 实时同步',
  deployment_config: 'Deployment 配置',
  environment_metadata: 'Environment 探测',
  manual_registry: 'Manual 登记',
}

const RENEWAL_URGENCY_LABEL: Record<string, { label: string; color: string }> = {
  expired: { label: '已过期', color: platformColors.danger },
  due_1d: { label: '1 天内到期', color: platformColors.danger },
  due_7d: { label: '7 天内到期', color: platformColors.warning },
  due_14d: { label: '14 天内到期', color: platformColors.warning },
  due_30d: { label: '30 天内到期', color: platformColors.info },
}

function HealthBadge({ health, stale }: { health: ExternalServiceHealth; stale?: boolean }) {
  const tone = HEALTH_TONE[health] ?? HEALTH_TONE.unknown
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          padding: '1px 8px',
          borderRadius: 4,
          background: tone.bg,
          color: tone.color,
          whiteSpace: 'nowrap',
        }}
      >
        {tone.icon} {tone.label}
      </span>
      {stale && (
        <Tooltip title="Provider 调用失败/未同步 — 以下为最近一次成功同步的数据">
          <Tag color="default" style={{ fontSize: 10, margin: 0 }}>
            stale
          </Tag>
        </Tooltip>
      )}
    </span>
  )
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (typeof value === 'number') return value.toLocaleString('zh-CN')
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** Generic, sorted key/value block — used for `usage`/`quota`/`metadata`
 * so this page never hardcodes a per-provider field name (spec §24). */
function KeyValueList({ data, emptyText = '暂无数据' }: { data: Record<string, unknown>; emptyText?: string }) {
  const entries = Object.entries(data ?? {}).filter(([k]) => !k.startsWith('_'))
  if (entries.length === 0) {
    return <div style={{ fontSize: 12, color: platformColors.mutedText }}>{emptyText}</div>
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ fontSize: 12, color: platformColors.secondaryText }}>{k}</div>
          <div style={{ fontSize: 12, color: platformColors.primaryText, wordBreak: 'break-word' }}>{renderValue(v)}</div>
        </div>
      ))}
    </div>
  )
}

function renewalSummary(row: ExternalServiceRow): string {
  const r = row.renewal
  if (!r) return '—'
  if (r.next_billing_date) return `下次账单：${formatDateTime(r.next_billing_date)}`
  if (r.expiration_date) return `到期日：${formatDateTime(r.expiration_date)}`
  if (r.token_expiry) return `Token 过期：${formatDateTime(r.token_expiry)}`
  return r.note || '—'
}

function DetailDrawer({ row, onClose, onRefreshed }: { row: ExternalServiceRow | null; onClose: () => void; onRefreshed: (r: ExternalServiceRow) => void }) {
  const [refreshing, setRefreshing] = useState(false)
  const [detail, setDetail] = useState<ExternalServiceRow | null>(row)

  useEffect(() => setDetail(row), [row])

  if (!detail) return null

  const doRefresh = () => {
    setRefreshing(true)
    platformRefreshExternalService(detail.id)
      .then((res) => {
        setDetail(res.data)
        onRefreshed(res.data)
        message.success('已刷新')
      })
      .catch((err) => message.error(err.message || '刷新失败'))
      .finally(() => setRefreshing(false))
  }

  const manualAction = (detail.metadata?.manual_action_required as string) || null

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>{detail.service_name}</span>
          <HealthBadge health={detail.health} stale={detail.stale} />
        </div>
      }
      open={!!row}
      onClose={onClose}
      width={520}
      extra={
        <button
          type="button"
          onClick={doRefresh}
          disabled={refreshing}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 28,
            padding: '0 10px',
            borderRadius: 6,
            border: `1px solid ${platformColors.border}`,
            background: '#fff',
            fontSize: 12,
            cursor: refreshing ? 'default' : 'pointer',
          }}
        >
          <ReloadOutlined spin={refreshing} /> 刷新
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: platformColors.secondaryText }}>
          <span>Provider：{detail.provider}</span>
          <span>类型：{detail.service_type}</span>
          <span>环境：{detail.environment}</span>
          <span>计费模式：{detail.billing_mode}</span>
          {detail.console_url && (
            <a href={detail.console_url} target="_blank" rel="noreferrer">
              打开控制台 ↗
            </a>
          )}
        </div>

        {detail.dependency_note && (
          <SectionCard title="影响范围（依赖关系）">
            <div style={{ fontSize: 12, color: platformColors.primaryText, padding: '4px 8px' }}>{detail.dependency_note}</div>
          </SectionCard>
        )}

        <SectionCard title="用量 Usage">
          <div style={{ padding: '4px 8px' }}>
            <KeyValueList data={detail.usage} emptyText="Provider 未返回可用用量数据" />
          </div>
        </SectionCard>

        <SectionCard title="配额 Quota">
          <div style={{ padding: '4px 8px' }}>
            <KeyValueList data={detail.quota} emptyText="无固定配额上限（usage-based 服务），或 Provider 未提供" />
          </div>
        </SectionCard>

        <SectionCard title="计费 / 续期">
          <div style={{ padding: '4px 8px', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div>模式：{detail.billing?.billing_mode ?? '—'}</div>
            <div>预估月费：{detail.billing?.estimated_monthly_charge ?? '—'}</div>
            <div>{renewalSummary(detail)}</div>
            {detail.renewal_urgency && RENEWAL_URGENCY_LABEL[detail.renewal_urgency] && (
              <Tag color="warning" style={{ width: 'fit-content' }}>
                {RENEWAL_URGENCY_LABEL[detail.renewal_urgency].label}
              </Tag>
            )}
          </div>
        </SectionCard>

        <SectionCard title="详细信息 Metadata">
          <div style={{ padding: '4px 8px' }}>
            <KeyValueList data={detail.metadata} />
          </div>
        </SectionCard>

        {manualAction && (
          <div
            style={{
              background: platformColors.warningBg,
              border: `1px solid ${platformColors.warning}`,
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              color: platformColors.warning,
            }}
          >
            <strong>MANUAL_ACTION_REQUIRED：</strong>
            {manualAction}
          </div>
        )}

        {detail.error && (
          <div style={{ fontSize: 12, color: platformColors.danger }}>最近一次同步错误：{detail.error}</div>
        )}

        <div style={{ fontSize: 11, color: platformColors.mutedText }}>
          数据来源：{SOURCE_LABEL[detail.source] ?? detail.source} · 最近同步：{formatRelativeTime(detail.last_sync)}
        </div>
      </div>
    </Drawer>
  )
}

export default function ExternalServicesPage() {
  const [data, setData] = useState<{ summary: ExternalServicesSummary; services: ExternalServiceRow[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<ExternalServiceRow | null>(null)

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    platformListExternalServices()
      .then((res) => {
        setData(res.data)
        setError(null)
      })
      .catch((err) => setError(err.message || '数据加载失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [load])

  const summary = data?.summary

  return (
    <div style={{ maxWidth: 1600 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>外部服务</div>
        <div style={{ fontSize: 12, lineHeight: '16px', color: platformColors.secondaryText, marginTop: 2 }}>
          Vistoera 真实依赖的第三方基础设施 / 服务清单 —— Provider API 可用时实时同步，不可用时诚实标注
          "Not configured" / "Unknown"，从不伪造数据
        </div>
      </div>

      {error && (
        <div
          style={{
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.dangerBg}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          数据加载失败：{error}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <SummaryCard
          title="服务总数"
          loading={loading && !data}
          value={summary ? summary.total : '—'}
          iconBg={cardIconBg.storage}
          icon={<ClockCircleOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title="Healthy"
          loading={loading && !data}
          value={summary ? summary.healthy : '—'}
          iconBg={cardIconBg.site}
          icon={<CheckCircleOutlined style={{ color: platformColors.success }} />}
        />
        <SummaryCard
          title="Not configured"
          loading={loading && !data}
          value={summary ? summary.not_configured : '—'}
          iconBg={cardIconBg.domain}
          icon={<QuestionCircleOutlined style={{ color: platformColors.secondaryText }} />}
        />
        <SummaryCard
          title="即将到期 / 续期"
          loading={loading && !data}
          value={summary ? summary.expiring : '—'}
          iconBg={cardIconBg.task}
          icon={<ExclamationCircleOutlined style={{ color: platformColors.warning }} />}
        />
      </div>

      <SectionCard title="服务清单">
        <Table<ExternalServiceRow>
          className="pf-data-table"
          sticky
          rowKey="id"
          loading={loading && !data}
          dataSource={data?.services ?? []}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          locale={{ emptyText: '暂无数据' }}
          onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Provider', dataIndex: 'provider', width: 100 },
            { title: '服务', dataIndex: 'service_name' },
            { title: '环境', dataIndex: 'environment', width: 90 },
            { title: '套餐', dataIndex: 'plan', render: (v: string | null) => v ?? '—', width: 100 },
            {
              title: '状态',
              key: 'health',
              width: 140,
              render: (_: unknown, row: ExternalServiceRow) => <HealthBadge health={row.health} stale={row.stale} />,
            },
            {
              title: '计费 / 续期',
              key: 'renewal',
              render: (_: unknown, row: ExternalServiceRow) => (
                <span style={{ fontSize: 12 }}>
                  {renewalSummary(row)}
                  {row.renewal_urgency && RENEWAL_URGENCY_LABEL[row.renewal_urgency] && (
                    <Tag color="warning" style={{ marginLeft: 6, fontSize: 10 }}>
                      {RENEWAL_URGENCY_LABEL[row.renewal_urgency].label}
                    </Tag>
                  )}
                </span>
              ),
            },
            {
              title: 'API 同步',
              key: 'sync',
              width: 160,
              render: (_: unknown, row: ExternalServiceRow) => (
                <Tooltip title={SOURCE_LABEL[row.source] ?? row.source}>
                  <span style={{ fontSize: 12 }}>{row.api_enabled ? formatRelativeTime(row.last_sync) : '未接入'}</span>
                </Tooltip>
              ),
            },
          ]}
        />
      </SectionCard>

      <DetailDrawer
        row={selected}
        onClose={() => setSelected(null)}
        onRefreshed={(updated) => {
          setData((prev) => (prev ? { ...prev, services: prev.services.map((s) => (s.id === updated.id ? updated : s)) } : prev))
        }}
      />
    </div>
  )
}
